import { format } from 'date-fns';
import type { VacationModality, VacationRequest, VacationRequestStatus } from './models/vacation.types';
import { RECEIPT_LINKABLE_STATUSES } from './utils/vacationReceiptLinks';

export const LEGACY_PERIODS_CUTOFF_DATE = '2099-01-01';

export const SCHEDULE_LIMIT_MONTHS = 11;

export interface StatusMetadata {
    label: string;
    className: string;
    rank: number;
}

export const STATUS_METADATA: Record<VacationRequestStatus, StatusMetadata> = {
    Draft: {
        label: 'Aguardando envio ao gestor',
        className: 'bg-gray-100 text-gray-700',
        rank: 0.5,
    },
    PendingManager: {
        label: 'Aprovação pendente - Gestor',
        className: 'bg-amber-100 text-amber-800',
        rank: 1,
    },
    PendingClient: {
        label: 'Aprovação pendente - Cliente',
        className: 'bg-amber-100 text-amber-800',
        rank: 1.5,
    },
    PendingDP: {
        label: 'Validação pendente - DP',
        className: 'bg-amber-100 text-amber-800',
        rank: 2,
    },
    PendingCorrection: {
        label: 'Necessário correção de período',
        className: 'bg-amber-100 text-amber-800',
        rank: 2.5,
    },
    Scheduled: {
        label: 'Agendada',
        className: 'bg-blue-100 text-blue-800',
        rank: 3,
    },
    OnVacation: {
        label: 'Em férias',
        className: 'bg-emerald-100 text-emerald-800',
        rank: 4,
    },
    Completed: {
        label: 'Concluída',
        className: 'bg-gray-100 text-gray-700',
        rank: 6,
    },
    Rejected: {
        label: 'Reprovada',
        className: 'bg-red-100 text-red-800',
        rank: 5,
    },
};

export const UNKNOWN_STATUS_RANK = 99;

export const UNKNOWN_STATUS_LABEL = 'Status não reconhecido';

export function getStatusMetadata(status: string): StatusMetadata {
    return (
        STATUS_METADATA[status as VacationRequestStatus] ?? {
            label: UNKNOWN_STATUS_LABEL,
            className: 'bg-gray-100 text-gray-700',
            rank: UNKNOWN_STATUS_RANK,
        }
    );
}

export const SCHEDULED_AWAITING_RECEIPT_LABEL = 'Agendada, aguardando recibo';

export const RECEIPT_PENDING_BADGE = 'Recibo pendente';

export function getRequestStatusMetadata(request: Pick<VacationRequest, 'status' | 'receipt'>): StatusMetadata {
    const metadata = getStatusMetadata(request.status);
    if (request.status === 'Scheduled' && !request.receipt) {
        return { ...metadata, label: SCHEDULED_AWAITING_RECEIPT_LABEL };
    }
    return metadata;
}

const RECEIPT_PENDING_BADGE_STATUSES: readonly VacationRequestStatus[] =
    RECEIPT_LINKABLE_STATUSES.filter((status) => status !== 'Scheduled');

export function isReceiptPending(
    request: Pick<VacationRequest, 'status' | 'receipt' | 'segments' | 'receiptSegmentOrders'>
): boolean {
    if (!RECEIPT_PENDING_BADGE_STATUSES.includes(request.status)) return false;
    if (!request.receipt) return true;

    const segments = request.segments ?? [];
    if (segments.length <= 1) return false;

    const ordersWithReceipt = request.receiptSegmentOrders;
    if (ordersWithReceipt === null) {
        return false;
    }

    if (!Array.isArray(ordersWithReceipt)) return true;

    const allOrders = new Set(segments.map((seg) => seg.order));
    const receivedOrders = new Set(ordersWithReceipt);

    for (const order of Array.from(allOrders)) {
        if (!receivedOrders.has(order)) return true;
    }

    return false;
}

export function getUncoveredReceiptSegments(
    request: Pick<VacationRequest, 'segments' | 'receipt' | 'receiptSegmentOrders'>,
    today: Date = new Date()
): Array<{ order: number; startDate: string; endDate: string }> {
    const segments = request.segments ?? [];
    if (segments.length <= 1) return [];
    if (!request.receipt) return segments;

    const ordersWithReceipt = request.receiptSegmentOrders;
    if (ordersWithReceipt === null) {
        return [];
    }

    const todayIso = format(today, 'yyyy-MM-dd');
    const receivedOrders = new Set(ordersWithReceipt ?? []);

    return segments.filter((seg) => !receivedOrders.has(seg.order) && seg.startDate <= todayIso);
}

export function getReceiptPendingLabel(
    request: Pick<VacationRequest, 'status' | 'segments' | 'receipt' | 'receiptSegmentOrders'>,
    today: Date = new Date()
): string | null {
    if (!RECEIPT_PENDING_BADGE_STATUSES.includes(request.status)) return null;

    const uncovered = getUncoveredReceiptSegments(request, today);
    if (uncovered.length === 0) return null;

    const segments = request.segments ?? [];
    if (segments.length <= 1) {
        return RECEIPT_PENDING_BADGE;
    }

    if (uncovered.length === 1) {
        const sorted = Array.from(segments).sort((a, b) => a.startDate.localeCompare(b.startDate));
        const position = sorted.findIndex((s) => s.order === uncovered[0].order) + 1;
        return `${RECEIPT_PENDING_BADGE} · ${position}º período`;
    }

    return 'Recibos pendentes';
}

export const MODALITY_LABELS: Record<string, string> = {
    '30': '30 dias corridos',
    '10+20': '10 + 20 dias',
    '15+15': '15 + 15 dias',
};

// O abono é decidido só pelo DP: o gestor não opina, então 'Pending' aponta para lá.
export const SELL_STATUS_LABELS: Record<string, string> = {
    None: '',
    Pending: 'Abono pecuniário: aguardando decisão do DP',
    Approved: 'Abono pecuniário aprovado',
    Rejected: 'Abono pecuniário reprovado',
};

export function getModalityLabel(modality: VacationModality): string {
    return MODALITY_LABELS[modality] ?? modality;
}

export function getCollaboratorProfileHref(collaboratorId: string): string {
    return `/collaborators/${collaboratorId}`;
}

export function getProfileLinkLabel(collaboratorName: string): string {
    return `Ver perfil de ${collaboratorName}`;
}

export const TIMELINE_LABELS = {
    requested: 'Solicitação enviada',
    draftRequested: 'Solicitação registrada',
    draftPending: 'Será enviada ao gestor ao fim do prazo de exclusão',
    managerPending: 'Aguardando aprovação do gestor',
    managerApproved: 'Aprovada pelo gestor',
    managerRejected: 'Reprovada pelo gestor',
    clientPending: 'Aprovação pendente - Cliente',
    clientApproved: 'Aprovada pelo cliente',
    clientRejected: 'Rejeitada pelo cliente',
    dpPending: 'Validação pendente - DP',
    correctionPending: 'Aguardando correção de período pelo colaborador',
    dpApproved: 'Validada pelo DP',
    dpRejected: 'Reprovada pelo DP',
    sellPending: 'Abono pecuniário: aguardando decisão do DP',
    sellApproved: 'Abono pecuniário aprovado',
    sellRejected: 'Abono pecuniário reprovado',
    signatureSent: 'Enviada para assinatura',
    signatureSigned: 'Documento assinado',
    started: 'Férias iniciadas',
    completed: 'Férias concluídas',
    expiredRejected: 'Expirada por prazo vencido',
} as const;

const SEGMENT_ORDINALS = ['1ª', '2ª', '3ª'] as const;

function getSegmentOrdinal(index: number): string {
    return SEGMENT_ORDINALS[index] ?? `${index + 1}ª`;
}

export function getSegmentStartedLabel(index: number, total: number): string {
    return total <= 1 ? TIMELINE_LABELS.started : `${getSegmentOrdinal(index)} parcela iniciada`;
}

export function getSegmentCompletedLabel(index: number, total: number): string {
    return total <= 1 ? TIMELINE_LABELS.completed : `${getSegmentOrdinal(index)} parcela concluída`;
}

export const PROJECTED_DEADLINE_HINT = 'Prazo do período aquisitivo em andamento';

export const CLIENT_CONFIRMED_BADGE = 'Cliente já confirmou';
export const CLIENT_NOT_CONFIRMED_BADGE = 'Cliente ainda não confirmou';

export function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts[0][0] ?? '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
    return `${first}${last}`.toUpperCase();
}
